/*------------------------------------------------------------------------------
   About      : Assignments tab — entity picker + two never-merged category
                 panels (Pages & Actions, API Permissions)

   Replaces the old monolithic EntityPermissionPanel: entity selection and the
   shared permission-tree fetch live here, each category panel owns its own
   pending/save state (design doc §5) and gets remounted (via `key`) on entity
   switch so unsaved edits never leak across entities.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useState, SyntheticEvent } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import {
  selectRoles,
  selectUsers,
  selectSelectedEntityType,
  selectSelectedEntityId,
  setRoles,
  setUsers,
  setSelectedEntity,
  setPermissionTree,
  setEnforcedModels,
  RbacEntityType,
} from 'store/dataReducers/rbacSlice';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { usePermissions } from 'hooks/usePermissions';
import { EntitySidebar } from './EntitySidebar';
import { ApiPermissionPanel } from './ApiPermissionPanel';
import { UiAccessPanel } from './UiAccessPanel';
import { AccessGroupAssignmentsPanel } from './AccessGroupAssignmentsPanel';
import { ModelPermissionsPanel } from './ModelPermissionsPanel';

type Category = 'pages' | 'api' | 'groups' | 'model';

const ACCESS_CONTROL_CTAS = UI_PAGES.ACCESS_CONTROL.ctas;

export const AssignmentsPanel = () => {
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const dispatch = useDispatch();
  const { can } = usePermissions();

  const roles = useSelector(selectRoles);
  const users = useSelector(selectUsers);
  const selectedEntityType = useSelector(selectSelectedEntityType);
  const selectedEntityId = useSelector(selectSelectedEntityId);

  const canSeePages = can(ACCESS_CONTROL_CTAS.view_pages_actions_category.id);
  const canSeeApi = can(ACCESS_CONTROL_CTAS.view_api_permissions_category.id);
  const canSeeGroupAssignments = can(ACCESS_CONTROL_CTAS.view_group_assignments_category.id);
  const canSeeModel = can(ACCESS_CONTROL_CTAS.view_model_permissions_category.id);

  const firstAllowedCategory = (): Category => {
    if (canSeePages) return 'pages';
    if (canSeeApi) return 'api';
    if (canSeeGroupAssignments) return 'groups';
    return 'model';
  };

  const [tab, setTab] = useState<RbacEntityType>('role');
  const [category, setCategory] = useState<Category>(firstAllowedCategory());
  const [treeLoading, setTreeLoading] = useState(false);

  const loadEntities = async () => {
    const [rolesResp, usersResp] = await Promise.all([
      RbacApis.getRoles(rc),
      RbacApis.getUsers(rc),
    ]);
    if (rolesResp.success && rolesResp.data) dispatch(setRoles(rolesResp.data));
    else if (rolesResp.error) toast.error(rolesResp.error);

    if (usersResp.success && usersResp.data) dispatch(setUsers(usersResp.data));
    else if (usersResp.error) toast.error(usersResp.error);
  };

  useEffect(() => {
    loadEntities();
    RbacApis.getEnforcedModels(rc).then((resp) => {
      if (resp.success) dispatch(setEnforcedModels(resp.data?.models || []));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPermissionTree = async (type: RbacEntityType, id: number | string) => {
    setTreeLoading(true);
    const resp = type === 'role'
      ? await RbacApis.getRolePermissionTree(rc, id)
      : await RbacApis.getUserPermissionTree(rc, id);
    if (resp.success) {
      dispatch(setPermissionTree(resp.data));
    } else if (resp.error) {
      toast.error(resp.error);
    }
    setTreeLoading(false);
  };

  const selectEntity = (type: RbacEntityType, id: number | string) => {
    dispatch(setSelectedEntity({ type, id }));
    fetchPermissionTree(type, id);
  };

  // Auto-select the first role so the panel is never empty on load.
  useEffect(() => {
    if (selectedEntityId === undefined && tab === 'role' && roles.length > 0) {
      selectEntity('role', roles[0].id);
    }
  }, [roles]);

  const handleTabChange = (value: RbacEntityType) => setTab(value);
  const handleCategoryChange = (_: SyntheticEvent, value: Category) => setCategory(value);
  const handleSelectEntity = (id: number | string) => selectEntity(tab, id);

  const refetchAfterSave = async () => {
    if (selectedEntityType === undefined || selectedEntityId === undefined) return;
    await Promise.all([fetchPermissionTree(selectedEntityType, selectedEntityId), loadEntities()]);
  };

  const hasSelection = selectedEntityId !== undefined;

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, height: '100%', minWidth: 0 }}>
      <EntitySidebar
        tab={tab}
        roles={roles}
        users={users}
        selectedEntityType={selectedEntityType}
        selectedEntityId={selectedEntityId}
        onTabChange={handleTabChange}
        onSelectEntity={handleSelectEntity}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!hasSelection && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Select a role or user to view and edit its access.
          </Typography>
        )}

        {hasSelection && (
          <>
            <Tabs value={category} onChange={handleCategoryChange} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
              {canSeePages && <Tab label="Pages & Actions" value="pages" />}
              {canSeeApi && <Tab label="API Permissions" value="api" />}
              {canSeeGroupAssignments && <Tab label="Access Groups" value="groups" />}
              {canSeeModel && <Tab label="Model Permissions" value="model" />}
            </Tabs>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              {treeLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading…</Typography>
              ) : category === 'pages' && canSeePages ? (
                <UiAccessPanel
                  key={`${selectedEntityType}:${selectedEntityId}`}
                  entityType={selectedEntityType!}
                  entityId={selectedEntityId!}
                  onSaved={refetchAfterSave}
                />
              ) : category === 'api' && canSeeApi ? (
                <ApiPermissionPanel
                  key={`${selectedEntityType}:${selectedEntityId}`}
                  entityType={selectedEntityType!}
                  entityId={selectedEntityId!}
                  onSaved={refetchAfterSave}
                />
              ) : category === 'groups' && canSeeGroupAssignments ? (
                <AccessGroupAssignmentsPanel
                  key={`${selectedEntityType}:${selectedEntityId}`}
                  entityType={selectedEntityType!}
                  entityId={selectedEntityId!}
                  onSaved={refetchAfterSave}
                />
              ) : category === 'model' && canSeeModel ? (
                <ModelPermissionsPanel
                  key={`${selectedEntityType}:${selectedEntityId}`}
                  entityType={selectedEntityType!}
                  entityId={selectedEntityId!}
                  onSaved={refetchAfterSave}
                />
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
